
































import Enumerable from "linq";
import InfiniteLoading from "vue-infinite-loading";
import { Component, Mixins, Prop, Watch, Emit } from "vue-property-decorator";
import { ElTree } from "element-ui/types/tree";
import {
  EditableFormatSummary,
  IEditableFormatSummaryTreeNode,
  IEditableFormatSummaryTreeData,
} from "../../../../../domain/editableFormat";
import { Directory } from "../../../../../../base/domain/drawing";
import { TreeNodeDropType } from "../../../../../../base/domain/system";
import { DirectoryTreeNodesFactory } from "../../../../../../base/components/system/utilities";
import { FormatTreeNodeUtility } from "../utilities";
import { BrowserUtility, BrowserKinds } from "../../../../../../../utilities";

@Component({
  components: {
    InfiniteLoading
  },
})
export default class EditableFormatSummaryTreeEditor extends Mixins() {
  private static readonly _nodeUtility = new FormatTreeNodeUtility();

  factory: DirectoryTreeNodesFactory<number, EditableFormatSummary> = null;
  chunkedCount: number = 0;
  loadedNodes: IEditableFormatSummaryTreeData[] = [];
  nodesData: IEditableFormatSummaryTreeData[] = [];
  selectedNode: IEditableFormatSummaryTreeNode = null;

  $refs!: {
    tree: ElTree<number, IEditableFormatSummaryTreeData>,
    infiniteLoading: InfiniteLoading
  };

  @Prop({ default: () => [] as Directory<number, EditableFormatSummary>[] })
  editingDirectories: Directory<number, EditableFormatSummary>[];

  @Prop({ default: 50 })
  loadChunkSize: number;

  mounted(): void {
    this.loadNodes();
  }

  onInfinite(): void {
    this.chunkList();
  }

  private chunkList(): void {
    for (let i = this.chunkedCount; i < (this.chunkedCount + this.loadChunkSize); i++) {
      if (i >= this.nodesData.length) {
        this.chunkedCount = this.nodesData.length;
        this.$refs.infiniteLoading.stateChanger.complete();
        return;
      }
      this.loadedNodes.push(this.nodesData[i]);
    }
    this.chunkedCount += this.loadChunkSize;
    this.$refs.infiniteLoading.stateChanger.loaded();
  }

  private resetChunkList(): void {
    this.loadedNodes = [];
    this.chunkedCount = 0;
    if (this.$refs.infiniteLoading) {
      this.$refs.infiniteLoading.stateChanger.reset();
    }
  }

  get fixedStyle(): any {
    // IEの場合、D&D中にフォルダ内のスクロールが動作しなかった。
    // 検証したところ横スクロールバーが表示されている状態なら動作した。
    // el-treeコンポーネントのCSSにoverflow:autoを移動させることでも実現できたが、
    // InfiniteLoadingが正しく動作しなくなった。（スクロールせずともinfiniteイベントが上がり続けてしまう）
    // el-tableのslot="append"をel-treeにも実装してもらえれば解決するのだが・・・
    return (BrowserUtility.getBrowserKind() === BrowserKinds.InternetExplorer)
      ? {
        overflow: "scroll"
      }
      : null;
  }

  @Watch("editingDirectories")
  onEditingDirectoriesChanged(newValue: Directory<number, EditableFormatSummary>[], oldValue: Directory<number, EditableFormatSummary>[]): void {
    this.loadNodes();
  }

  private loadNodes(): void {
    // thisをバインドしておかないと実行時のthisが変わるのでアロー関数でバインド.
    this.factory = new DirectoryTreeNodesFactory(
      (e: EditableFormatSummary, i: number) => EditableFormatSummaryTreeEditor._nodeUtility.toFormatNode(e, i));
    // nodesを算出プロパティで処理するとツリーの変更が行われるたびに呼び出され、ツリーの開閉状態が初期化されてしまうため分離した
    this.nodesData = this.factory.create(this.editingDirectories);
    this.selectedNode = null;
    this.resetChunkList();
  }

  overwriteNodes(directory: { [key: number]: Directory<number, EditableFormatSummary> }, summary: { [key: number]: EditableFormatSummary }): void {
    // データを上書きするだけ。配列の並びや増減に関しては考慮しない
    this.overwriteNodesData(this.nodesData, directory, summary);
    this.overwriteNodesData(this.loadedNodes, directory, summary);
  }

  private overwriteNodesData(nodes: IEditableFormatSummaryTreeData[], directory: { [key: number]: Directory<number, EditableFormatSummary> }, summary: { [key: number]: EditableFormatSummary }): void {
    Enumerable.from(nodes).forEach(n => {
      if (n.source instanceof Directory && directory[n.source.key] instanceof Directory) {
        n.source = directory[n.source.key];
      } else if (n.source instanceof EditableFormatSummary && summary[n.source.id] instanceof EditableFormatSummary) {
        n.source = summary[n.source.id];
      }
      this.overwriteNodesData(n.children, directory, summary);
    });
  }

  nodeClass(node: IEditableFormatSummaryTreeNode): string {
    return EditableFormatSummaryTreeEditor._nodeUtility.nodeIconClass(node);
  }

  nodeLabel(node: IEditableFormatSummaryTreeNode): string {
    return node.label;
  }

  shouldShowNodeKey(data: IEditableFormatSummaryTreeData): boolean {
    return data.source instanceof Directory && !!data.source.key;
  }

  nodeKey(data: IEditableFormatSummaryTreeData): string {
    return this.shouldShowNodeKey(data) && data.source instanceof Directory
      ? " (" + data.source.key + ")"
      : "";
  }

  shouldShowNodeId(data: IEditableFormatSummaryTreeData): boolean {
    return data.source instanceof EditableFormatSummary && !!data.source.id;
  }

  nodeId(data: IEditableFormatSummaryTreeData): string {
    return this.shouldShowNodeId(data) && data.source instanceof EditableFormatSummary
      ? " (" + data.source.id + ")"
      : "";
  }

  handleNodeClicked(nodeObject: IEditableFormatSummaryTreeData, nodeProperty: IEditableFormatSummaryTreeNode, nodeSelf: any): void {
    const source = nodeObject.source;
    if (source instanceof Directory) {
      this.selectedDirectoryUpdated(source);
    }
    if (source instanceof EditableFormatSummary) {
      this.selectedFormatUpdated(source);
    }
    if (nodeProperty.childNodes instanceof Array && nodeProperty.childNodes.length === 0) {
      nodeProperty.expanded = false;  // 移動等でファイルが無くなっていた場合はとりあえず閉じる
    }
    this.selectedNode = nodeProperty;
  }

  setNodeLabel(lable: string): void {
    if (this.selectedNode === null) {
      return;
    }
    const data = this.selectedNode.data;
    if (data.source instanceof Directory) {
      data.label = lable;
    } else if (data.source instanceof EditableFormatSummary) {
      data.label = lable;
    }
  }

  addFolder(key: number, lable: string): void {
    // 共通のpublishIdを使用するためfactory経由でデータを作成
    const dir = [
      new Directory<number, EditableFormatSummary>(key, lable, [], []
    )];
    const nodes = this.factory.create(dir);
    this.addNode(nodes[0]);
  }

  private addNode(childData: IEditableFormatSummaryTreeData): void {
    if (this.selectedNode === null) {
      const node = this.$refs.tree as any; // rootが必要なのでanyに変換
      this.$refs.tree.append(childData, node.root); // infiniteで読み込み終わっていない場合途中に追加されてしまうので対策出来ればやりたい
    } else {
      this.$refs.tree.append(childData, this.selectedNode.data);
      this.sortNode(this.selectedNode);
      this.selectedNode.expanded = true;  // 追加したので開く
    }
  }

  deleteNode(): void {
    if (this.selectedNode === null) {
      return;
    }
    this.$refs.tree.remove(this.selectedNode.data);
    this.selectedNode = null;
  }

  allowDrag(draggingNode: IEditableFormatSummaryTreeNode): boolean {
    return true;
  }

  allowDrop(draggingNode: IEditableFormatSummaryTreeNode, dropNode: IEditableFormatSummaryTreeNode, type: TreeNodeDropType): boolean {
    const dropNodeSource = dropNode.data.source;
    if (dropNodeSource instanceof EditableFormatSummary
      && type === "inner") {
      return false; // フォーマットの内側は全て禁止
    }
    const draggingNodeSource = draggingNode.data.source;
    if (draggingNodeSource instanceof EditableFormatSummary
      && dropNodeSource instanceof Directory
      && type !== "inner") {
      return false; // フォーマットをフォルダにドロップする場合、内側以外禁止
    }
    return true;
  }

  nodeDrop(draggingNode: IEditableFormatSummaryTreeNode, dropNode: IEditableFormatSummaryTreeNode, type: TreeNodeDropType): void {
    const parentDropNode = (type === "inner")
      ? dropNode          // ドロップ先が親になる
      : dropNode.parent;  // ドロップした親
    this.sortNode(parentDropNode);

    const dropNodeSource = dropNode.data.source;
    if (dropNodeSource instanceof Directory
      && type === "inner") {
      dropNode.expanded = true; // フォルダ内にドロップしたので開く
    }
    this.$refs.tree.setCurrentKey(this.selectedNode !== null ? this.selectedNode.key : null); // 表示上消えるので選択し直し

    this.notifyMovedNode(draggingNode, parentDropNode);
  }

  private sortNode(node: IEditableFormatSummaryTreeNode): void {
    if (node.childNodes instanceof Array) {
      node.childNodes.sort((a: IEditableFormatSummaryTreeNode, b: IEditableFormatSummaryTreeNode) => {
        const sourceA = a.data.source instanceof Directory ? 0 : 1;
        const sourceB = b.data.source instanceof Directory ? 0 : 1;
        return sourceA - sourceB;
      });
    }
    if (node.data !== null && node.data.children instanceof Array) {
      node.data.children.sort((a: IEditableFormatSummaryTreeData, b: IEditableFormatSummaryTreeData) => {
        const sourceA = a.source instanceof Directory ? 0 : 1;
        const sourceB = b.source instanceof Directory ? 0 : 1;
        return sourceA - sourceB;
      });
    }
  }

  private notifyMovedNode(draggingNode: IEditableFormatSummaryTreeNode, parentDropNode: IEditableFormatSummaryTreeNode): void {
    const parentDropNodeSource = parentDropNode.level > 0 && parentDropNode.data.source instanceof Directory
      ? parentDropNode.data.source
      : null;
    const draggingNodeSource = draggingNode.data.source;
    if (draggingNodeSource instanceof Directory) {
      const directories = Enumerable.from(parentDropNode.childNodes)
        .where(e => e.data.source instanceof Directory).select(e => e.data.source as Directory<number, EditableFormatSummary>).toArray();
      const targetIndex = Enumerable.from(directories).indexOf(e => e.key === draggingNodeSource.key);
      let index = 1;
      if (1 < directories.length) {
        // 移動先が末尾であれば後ろから2つ前のsort+1を並び順とする
        index = (targetIndex === (directories.length - 1)) ? directories[targetIndex - 1].sort + 1  : directories[targetIndex + 1].sort;
      }
      this.movedDirectory(draggingNodeSource, parentDropNodeSource, index);
    } else {
      const formats = Enumerable.from(parentDropNode.childNodes)
        .where(e => e.data.source instanceof EditableFormatSummary).select(e => e.data.source as EditableFormatSummary).toArray();
      const targetIndex = Enumerable.from(formats).indexOf(e => e.id === draggingNodeSource.id);
      let index = 1;
      if (1 < formats.length) {
        // 移動先が末尾であれば後ろから2つ前のsort+1を並び順とする
        index = (targetIndex === (formats.length - 1)) ? formats[targetIndex - 1].sort + 1 : formats[targetIndex + 1].sort;
      }
      this.movedFormat(draggingNodeSource, parentDropNodeSource, index);
    }
  }

  @Emit("selectedDirectoryUpdated")
  selectedDirectoryUpdated(value: Directory<number, EditableFormatSummary>): void {}

  @Emit("selectedFormatUpdated")
  selectedFormatUpdated(value: EditableFormatSummary): void {}

  @Emit("movedDirectory")
  movedDirectory(value: Directory<number, EditableFormatSummary>, parent: Directory<number, EditableFormatSummary>, index: number): void {}

  @Emit("movedFormat")
  movedFormat(value: EditableFormatSummary, parent: Directory<number, EditableFormatSummary>, index: number): void {}
}
